/**
 * Pure booking-submission validation for the photography booking site.
 *
 * This module is the single source of truth for validating a raw booking
 * inquiry submission BEFORE any availability re-check or persistence. It runs
 * unchanged in the browser (client-side form validation) and in Lambda
 * (authoritative server-side validation), built on the shared schema module
 * (`FIELD_LIMITS`, `EMAIL_PATTERN` via `isValidEmail` / `isWithinLimit`).
 *
 * It enforces these rejection conditions (Req 8.2, 8.3, 14.4):
 *
 *   - a required field is missing/blank                              (Req 8.2)
 *   - the email does not conform to a valid email format             (Req 8.3)
 *   - a field exceeds its configured maximum length                 (Req 14.4)
 *   - the session start time is missing or not a 24h "HH:MM" value
 *   - the session duration is missing, non-integer, or out of range
 *
 * Every returned error identifies the specific offending field and the rule
 * it violated, so callers can surface a precise, field-level message.
 */
import { FIELD_LIMITS } from "../schema/constraints.js";
import { isValidEmail, isWithinLimit } from "../schema/validators.js";
/**
 * The free-text/string fields that MUST be present (and non-blank) on a
 * submission. `startTime` and `durationMinutes` are required too but are
 * validated by their own dedicated rules (they carry format/range semantics),
 * so they are not part of this generic blank-check list.
 */
export const REQUIRED_BOOKING_FIELDS = [
    "clientName",
    "clientEmail",
    "requestedDate",
    "message",
];
/** Friendly, human-facing labels for each field (used in error messages). */
export const BOOKING_FIELD_LABELS = {
    clientName: "Name",
    clientEmail: "Email",
    requestedDate: "Date",
    startTime: "Start time",
    durationMinutes: "Session length",
    message: "Session details",
};
/** Friendly prompt shown when a required field is left blank. */
const MISSING_FIELD_MESSAGES = {
    clientName: "Please enter your name.",
    clientEmail: "Please enter your email address.",
    requestedDate: "Please choose an available date.",
    message: "Please tell us a little about your session.",
};
/** 24h "HH:MM" time-of-day pattern (00:00 .. 23:59). */
export const START_TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;
/** Inclusive minimum session duration in minutes. */
export const MIN_DURATION_MINUTES = 15;
/** Inclusive maximum session duration in minutes (12 hours). */
export const MAX_DURATION_MINUTES = 720;
/** The specific rule a field violated. */
export const BookingValidationRule = {
    /** A required field was absent or blank. */
    MISSING_REQUIRED_FIELD: "MISSING_REQUIRED_FIELD",
    /** The email value is not a syntactically valid email address. */
    INVALID_EMAIL_FORMAT: "INVALID_EMAIL_FORMAT",
    /** The field value is longer than its configured maximum length. */
    EXCEEDS_MAX_LENGTH: "EXCEEDS_MAX_LENGTH",
    /** The start time is missing or not a valid 24h "HH:MM" value. */
    INVALID_START_TIME: "INVALID_START_TIME",
    /** The duration is missing, not an integer, or outside the allowed range. */
    INVALID_DURATION: "INVALID_DURATION",
};
/**
 * Fields subject to a configured maximum length. `requestedDate` has no
 * free-text length limit (its shape is validated elsewhere), so it is omitted.
 */
const LENGTH_LIMITED_FIELDS = [
    "clientName",
    "clientEmail",
    "message",
];
/** True iff `value` is absent or contains only whitespace. */
function isBlank(value) {
    return value === undefined || value.trim().length === 0;
}
/**
 * Validates a raw booking submission.
 *
 * Returns `{ valid: true }` when the submission satisfies every rule, or
 * `{ valid: false, errors }` listing each violation. A submission is rejected
 * if and only if a required field is missing, the email is malformed, or any
 * field exceeds its configured maximum length (Req 8.2, 8.3, 14.4).
 *
 * All applicable errors are collected (validation does not stop at the first
 * failure) so the caller can report every problem at once. At most one error
 * is reported per (field, rule) pair.
 */
export function validateBookingSubmission(submission) {
    const errors = [];
    // 1. Required-field presence (Req 8.2).
    for (const field of REQUIRED_BOOKING_FIELDS) {
        if (isBlank(submission[field])) {
            errors.push({
                field,
                rule: BookingValidationRule.MISSING_REQUIRED_FIELD,
                message: MISSING_FIELD_MESSAGES[field],
            });
        }
    }
    // 2. Email format (Req 8.3). Only meaningful when an email was provided;
    //    a blank email is already reported as a missing required field.
    if (!isBlank(submission.clientEmail) && !isValidEmail(submission.clientEmail)) {
        errors.push({
            field: "clientEmail",
            rule: BookingValidationRule.INVALID_EMAIL_FORMAT,
            message: "Please enter a valid email address (for example, name@example.com).",
        });
    }
    // 3. Per-field maximum lengths (Req 14.4). Checked whenever a value is
    //    present, independent of whether other rules also failed.
    for (const field of LENGTH_LIMITED_FIELDS) {
        const value = submission[field];
        if (value !== undefined && !isWithinLimit(field, value)) {
            errors.push({
                field,
                rule: BookingValidationRule.EXCEEDS_MAX_LENGTH,
                message: `Your ${BOOKING_FIELD_LABELS[field].toLowerCase()} is too long (maximum ${FIELD_LIMITS[field]} characters).`,
            });
        }
    }
    // 4. Session start time: required and a valid 24h "HH:MM" value. A missing,
    //    blank, or malformed value is reported under the same INVALID_START_TIME
    //    rule with a precise message.
    const startTime = submission.startTime;
    if (isBlank(startTime)) {
        errors.push({
            field: "startTime",
            rule: BookingValidationRule.INVALID_START_TIME,
            message: "Please enter a start time and choose AM or PM.",
        });
    }
    else if (!START_TIME_PATTERN.test(startTime)) {
        errors.push({
            field: "startTime",
            rule: BookingValidationRule.INVALID_START_TIME,
            message: "Please enter a valid time such as 3:30, and choose AM or PM.",
        });
    }
    // 5. Session duration: required, an integer, and within the allowed range.
    const durationMinutes = submission.durationMinutes;
    if (durationMinutes === undefined) {
        errors.push({
            field: "durationMinutes",
            rule: BookingValidationRule.INVALID_DURATION,
            message: "Please choose a session length.",
        });
    }
    else if (!Number.isInteger(durationMinutes) ||
        durationMinutes < MIN_DURATION_MINUTES ||
        durationMinutes > MAX_DURATION_MINUTES) {
        errors.push({
            field: "durationMinutes",
            rule: BookingValidationRule.INVALID_DURATION,
            message: `Please choose a session length between ${MIN_DURATION_MINUTES} minutes and ${MAX_DURATION_MINUTES / 60} hours.`,
        });
    }
    return errors.length === 0 ? { valid: true } : { valid: false, errors };
}
//# sourceMappingURL=booking-validation.js.map